import React, { createContext, useContext, useState, useMemo, ReactNode, useEffect } from 'react';
import { Transaction } from '../../TransactionsTable/TransactionsTable';
import { ReportTransactionDataRow, AskRequest } from '@hone-automation/common';
import { AttachmentMetadata } from 'hooks/useAttachmentsModal';
import { EnhancedAskRequest } from 'hooks/useTransactionDetails';
import { ReportRow } from 'types/report';

interface TransactionsTableContextState {
  sortKey: string;
  setSortKey: (key: string) => void;
  sortDir: 'asc' | 'desc';
  setSortDir: (dir: 'asc' | 'desc') => void;
  reportType?: string;
  search: string;
  setSearch: (search: string) => void;
  total: number;
  balance: number;
  transactions: Transaction[];
  setTransactions: (transactions: Transaction[]) => void;
  reportRows: ReportTransactionDataRow[];
  attachmentsData?: ReportTransactionDataRow[];
  flaggedTransactions?: EnhancedAskRequest[] | undefined;
  setAttachmentsData: (data: ReportTransactionDataRow[]) => void;
  attachmentsLoading?: boolean;
  setAttachmentsLoading: (loading: boolean) => void;
  showAttachment: (attachments: string[], attachmentMetadata: AttachmentMetadata) => void;
  breadcrumb?: {
    parent?: string;
    date: {
      start: string;
      end: string;
    };
  };
  selectedTransaction: ReportRow | null;
  flaggedCells: AskRequest[];
  cellWithOutTransactions: boolean;
  resolvedLocationId?: string;
  allowFlagging?: boolean;
  isConsolidated?: boolean;
}

export const mappedTypes = {
  'Journal Entry': 'JE',
  Expense: 'EXP',
  Bill: 'BILL',
  Deposit: 'DEP',
  'Vendor Credit': 'VC',
};

export const mappedTypesTitle = {
  JE: 'Journal Entry',
  EXP: 'Expense',
  BILL: 'Bill',
  DEP: 'Deposit',
  VC: 'Vendor Credit',
};

const TransactionsTableContext = createContext<TransactionsTableContextState | undefined>(undefined);

interface ProviderProps {
  children: ReactNode;
  initialSortKey?: string;
  initialSortDir?: 'asc' | 'desc';
  initialTransactions?: Transaction[];
  reportType?: HoneReportType;
  total: number;
  balance: number;
  dataRows: ReportTransactionDataRow[];
  initialflaggedTransactions?: EnhancedAskRequest[] | undefined;
  showAttachment: (attachments: string[], attachmentMetadata: AttachmentMetadata) => void;
  intitialSelectedTransaction?: ReportRow | null;
  initialFlaggedCells?: AskRequest[] | undefined;
  resolvedLocationId?: string;
  allowFlagging?: boolean;
  isConsolidated?: boolean;
}

export const TransactionsTableProvider: React.FC<ProviderProps> = ({
  children,
  initialSortKey = 'date',
  initialSortDir = 'asc',
  reportType,
  initialTransactions = [],
  total,
  balance,
  dataRows = [],
  initialflaggedTransactions,
  showAttachment,
  intitialSelectedTransaction,
  initialFlaggedCells,
  resolvedLocationId,
  allowFlagging = true,
  isConsolidated = false,
}) => {
  const [sortKey, setSortKey] = useState<string>(initialSortKey);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(initialSortDir);
  const [search, setSearch] = useState<string>('');
  const [transactions, setTransactions] = useState<Transaction[]>(initialTransactions || []);
  const [reportRows, setReportRows] = useState<ReportTransactionDataRow[]>(dataRows || []);
  const [attachmentsLoading, setAttachmentsLoading] = useState<boolean>(false);
  const [attachmentsData, setAttachmentsData] = useState<ReportTransactionDataRow[] | undefined>([]);
  const [flaggedTransactions, setFlaggedTransactions] = useState<EnhancedAskRequest[] | undefined>(
    initialflaggedTransactions
  );
  const [selectedTransaction, setSelectedTransaction] = useState<ReportRow | null>(intitialSelectedTransaction ?? null);
  const [flaggedCells, setFlaggedCells] = useState<AskRequest[]>(initialFlaggedCells || []);
  const noDataRows = !dataRows || dataRows.length === 0;
  const [cellWithOutTransactions, setCellWithOutTransactions] = useState<boolean>(noDataRows);

  useEffect(() => {
    setTransactions(initialTransactions);
    setSearch('');
  }, [initialTransactions]);

  useEffect(() => {
    if (dataRows.length === 0) {
      setCellWithOutTransactions(true);
      return;
    }
    setCellWithOutTransactions(false);
    setReportRows(dataRows);
  }, [dataRows]);

  useEffect(() => {
    setAttachmentsLoading(attachmentsLoading);
  }, [attachmentsLoading]);

  useEffect(() => {
    setAttachmentsData(attachmentsData);
  }, [attachmentsData]);

  useEffect(() => {
    setFlaggedTransactions(initialflaggedTransactions);
  }, [initialflaggedTransactions]);

  useEffect(() => {
    setSelectedTransaction(intitialSelectedTransaction ?? null);
  }, [intitialSelectedTransaction]);

  useEffect(() => {
    setFlaggedCells(initialFlaggedCells || []);
  }, [initialFlaggedCells]);

  const value = useMemo(
    () => ({
      sortKey,
      setSortKey,
      sortDir,
      setSortDir,
      reportType,
      total,
      balance,
      search,
      setSearch: (search: string) => {
        setSearch(search);
        const normalizedSearch = search.toLowerCase();

        const filtered = [...initialTransactions].filter(trx => {
          const { name, type, memo, reference } = trx;
          return (
            name.toLowerCase().includes(normalizedSearch) ||
            type.toLowerCase().includes(normalizedSearch) ||
            memo?.toLowerCase().includes(normalizedSearch) ||
            reference?.toLowerCase().includes(normalizedSearch)
          );
        });
        setTransactions(filtered);
      },
      transactions,
      setTransactions,
      reportRows,
      attachmentsData,
      flaggedTransactions,
      setAttachmentsData,
      attachmentsLoading,
      setAttachmentsLoading,
      showAttachment,
      selectedTransaction,
      flaggedCells,
      cellWithOutTransactions,
      resolvedLocationId,
      allowFlagging,
      isConsolidated,
    }),
    [
      sortKey,
      sortDir,
      reportType,
      total,
      balance,
      search,
      setSearch,
      transactions,
      setTransactions,
      resolvedLocationId,
      allowFlagging,
      isConsolidated,
      flaggedCells,
      selectedTransaction,
      flaggedTransactions,
      reportRows,
      attachmentsData,
      attachmentsLoading,
      cellWithOutTransactions,
    ]
  );

  return <TransactionsTableContext.Provider value={value}>{children}</TransactionsTableContext.Provider>;
};

export const useTransactionsTableContext = () => {
  const context = useContext(TransactionsTableContext);
  if (!context) {
    throw new Error('useTransactionsTableContext must be used within a TransactionsTableProvider');
  }
  return context;
};
